import type { IBeneficiaryProjectRepository } from "@/application/ports/repositories";
import type { ApiResponse } from "../client";
import type {
  Project,
  CreateBeneficiaryProjectRequest,
  UpdateBeneficiaryProjectRequest,
  BeneficiaryProjectsResponse,
  ProjectBulkCreateRequest,
  ProjectBulkCreateResponse,
} from "@/domain";
import { apiClient } from "../client";
import { getLogger } from "@/infrastructure/di/services";

export class BeneficiaryProjectRepository implements IBeneficiaryProjectRepository {
  async getBeneficiaryProjects(
    beneficiaryUuid: string,
    filters?: {
      search?: string;
      type?: string;
      status?: string;
      documentation_status?: string;
      page?: number;
      limit?: number;
    },
  ): Promise<ApiResponse<BeneficiaryProjectsResponse>> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            params.append(key, value.toString());
          }
        });
      }

      const queryString = params.toString();
      const url = `/projects/beneficiaries/${beneficiaryUuid}/projects${queryString ? `?${queryString}` : ""}`;

      const response = await apiClient.get<BeneficiaryProjectsResponse>(url);
      return response;
    } catch (error) {
      getLogger().error("Error fetching beneficiary projects:", error);
      throw error;
    }
  }
  async getBeneficiaryProject(
    beneficiaryUuid: string,
    projectId: string,
  ): Promise<ApiResponse<Project>> {
    try {
      const response = await apiClient.get<Project>(
        `/projects/beneficiaries/${beneficiaryUuid}/projects/${projectId}`,
      );
      return response;
    } catch (error) {
      getLogger().error("Error fetching beneficiary project:", error);
      throw error;
    }
  }
  async createBeneficiaryProject(
    beneficiaryUuid: string,
    projectData: CreateBeneficiaryProjectRequest,
  ): Promise<ApiResponse<Project>> {
    try {
      const response = await apiClient.post<Project>(
        `/beneficiaries/${beneficiaryUuid}/projects`,
        projectData,
      );
      return response;
    } catch (error) {
      getLogger().error("Error creating beneficiary project:", error);
      throw error;
    }
  }
  async bulkCreateBeneficiaryProjects(
    beneficiaryUuid: string,
    payload: ProjectBulkCreateRequest,
  ): Promise<ApiResponse<ProjectBulkCreateResponse>> {
    try {
      const response = await apiClient.post<ProjectBulkCreateResponse>(
        `/beneficiaries/${beneficiaryUuid}/projects/bulk`,
        payload,
      );
      return response;
    } catch (error) {
      getLogger().error("Error bulk-creating beneficiary projects:", error);
      throw error;
    }
  }
  async updateBeneficiaryProject(
    beneficiaryUuid: string,
    projectId: string,
    projectData: UpdateBeneficiaryProjectRequest,
  ): Promise<ApiResponse<Project>> {
    try {
      const response = await apiClient.put<Project>(
        `/projects/beneficiaries/${beneficiaryUuid}/projects/${projectId}`,
        projectData,
      );
      return response;
    } catch (error) {
      getLogger().error("Error updating beneficiary project:", error);
      throw error;
    }
  }
  async deleteBeneficiaryProject(beneficiaryUuid: string, projectId: string): Promise<void> {
    try {
      await apiClient.delete(`/projects/beneficiaries/${beneficiaryUuid}/projects/${projectId}`);
    } catch (error) {
      getLogger().error("Error deleting beneficiary project:", error);
      throw error;
    }
  }
}
